if (!customElements.get('shop-the-look-slider')) {
  class ShopTheLookSlider extends customElements.get(
    'card-product-slider'
  ) {
    constructor() {
      super();

      this.dotsList = this.querySelector('.js-dots-list');
      this.pagination = this.querySelector('.shop-the-look--pagination');
      this.nextArrow = this.querySelector('.shop-the-look--next');
      this.prevArrow = this.querySelector('.shop-the-look--prev');
      this.swiperElement = this.querySelector('.js-slider');
      this._hasInitialized = false;
      this._visibilityObserver = null;

      this.sliderOptions.slidesPerView = 1;
      this.sliderOptions.spaceBetween = 0;
      this.sliderOptions.watchOverflow = false;

      this.sliderOptions.breakpoints = {
        750: {
          slidesPerView: 1,
          spaceBetween: 0
        },
        990: {
          spaceBetween: 0
        },
        1100: {
          spaceBetween: 0
        },
        1200: {
          spaceBetween: 0
        }
      };

      this.sliderOptions.pagination = {
        el: this.pagination,
        type: 'progressbar'
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.sliderOptions.autoplay = false;
      }

      this.onDotsClick = this.onDotsClick.bind(this);
      this.onSlideChange = this.onSlideChange.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
    }

    connectedCallback() {
      this.dotsList?.addEventListener('click', this.onDotsClick);
      this.initSliderWhenVisible();
    }

    disconnectedCallback() {
      this._visibilityObserver?.disconnect();
      this._visibilityObserver = null;
      if (this._swiperRetryTimer) {
        clearTimeout(this._swiperRetryTimer);
        this._swiperRetryTimer = null;
      }
      this.dotsList?.removeEventListener('click', this.onDotsClick);
      this.prevArrow?.removeEventListener('click', this.onPrevClick);
      this.nextArrow?.removeEventListener('click', this.onNextClick);
      this.slider?.off('slideChange', this.onSlideChange);
    }

    initSliderWhenVisible() {
      if (this._hasInitialized || this.slider) return;

      if (Shopify.designMode || !('IntersectionObserver' in window)) {
        this.init();
        return;
      }

      if (this._visibilityObserver) return;

      this._visibilityObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            this.init();
            if (!this.slider && !this._hasInitialized) return;
            this._visibilityObserver.disconnect();
            this._visibilityObserver = null;
          });
        },
        { rootMargin: '140px 0px', threshold: 0.01 }
      );

      this._visibilityObserver.observe(this);
    }

    scheduleSwiperRetry() {
      if (this._swiperRetryTimer || this.slider) return;

      let attempts = 0;
      const retry = () => {
        this._swiperRetryTimer = null;
        if (this.slider || this._hasInitialized || attempts >= 60) return;
        attempts += 1;
        this.init();
        if (!this.slider && !this._hasInitialized) {
          this._swiperRetryTimer = setTimeout(retry, 100);
        }
      };

      this._swiperRetryTimer = setTimeout(retry, 100);
    }

    init() {
      if (this.slider) return;

      this.prevArrow = this.querySelector('.shop-the-look--prev');
      this.nextArrow = this.querySelector('.shop-the-look--next');
      this.pagination = this.querySelector('.shop-the-look--pagination');
      this.swiperElement = this.querySelector('.js-slider');
      this.dotsList = this.querySelector('.js-dots-list');

      if (!this.swiperElement) {
        this._hasInitialized = true;
        this.initDrawer();
        return;
      }

      const slideCount = this.swiperElement.querySelectorAll(
        ':scope > .swiper-wrapper > .swiper-slide'
      ).length;

      if (slideCount === 0) {
        this._hasInitialized = true;
        this.initDrawer();
        return;
      }

      if (slideCount < 2) {
        this.sliderOptions.loop = false;
        this.sliderOptions.autoplay = false;
      }

      if (this.pagination) {
        this.sliderOptions.pagination = {
          el: this.pagination,
          type: 'progressbar'
        };
      } else {
        this.sliderOptions.pagination = false;
      }

      if (typeof Swiper === 'undefined') {
        this.scheduleSwiperRetry();
        return;
      }

      this.initSlider(this.swiperElement);

      if (!this.slider) {
        this.scheduleSwiperRetry();
        return;
      }

      this.slider.on('slideChange', this.onSlideChange);
      this.prevArrow?.addEventListener('click', this.onPrevClick);
      this.nextArrow?.addEventListener('click', this.onNextClick);
      this.syncNavButtons();
      this._hasInitialized = true;
      this.initDrawer();
    }

    onPrevClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.slider?.slidePrev();
    }

    onNextClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.slider?.slideNext();
    }

    syncNavButtons() {
      if (!this.slider) return;

      const loopEnabled = Boolean(this.slider.params.loop);
      const prevDisabled = !loopEnabled && this.slider.isBeginning;
      const nextDisabled = !loopEnabled && this.slider.isEnd;

      this.prevArrow?.classList.toggle('swiper-button-disabled', prevDisabled);
      this.nextArrow?.classList.toggle('swiper-button-disabled', nextDisabled);
      this.prevArrow?.toggleAttribute('disabled', prevDisabled);
      this.nextArrow?.toggleAttribute('disabled', nextDisabled);
    }

    onDotsClick(e) {
      const isBtn = e.target.classList.contains('js-btn');
      if (!isBtn || !this.slider) return;

      e.preventDefault();

      const btn = e.target;
      const isCurrent = btn.classList.contains('is-current');
      if (isCurrent) return;

      const btnIndex = +btn.dataset.index;
      this.updateActiveDot(btn);
      this.slider.slideTo(btnIndex, 300);
    }

    onSlideChange(e) {
      const sliderIndex = e.realIndex;
      const btn = this.dotsList?.querySelector(
        `.js-btn[data-index="${sliderIndex}"]`
      );
      this.updateActiveDot(btn);
      this.syncNavButtons();
    }

    updateActiveDot(btn) {
      const currentBtn = this.dotsList?.querySelector(
        '.js-btn.is-current'
      );

      if (!currentBtn || !btn) return;

      currentBtn.classList.remove('is-current');
      currentBtn.setAttribute('aria-current', false);

      btn.classList.add('is-current');
      btn.setAttribute('aria-current', true);
    }

    initDrawer() {
      const drawer = document.querySelector(
        ".shop-the-look-drawer__blocks"
      );
      const drawerContainer = document.querySelector(
        "shop-the-look-drawer"
      );
      if (!drawer || !drawerContainer) return;
    }
  }

  customElements.define('shop-the-look-slider', ShopTheLookSlider);
}
